import {createSelector} from 'reselect';
import request, {CancelTokenSource} from 'axios';
import {omit} from 'ramda';

import {createAction, createReducer} from 'redux-act';

import {ENDPOINTS} from 'constants/endpoints';
import {AddToCartType} from 'pages/cart/store/actions/addToCart/addToCartType';
import {ApplicationStoreType} from 'reducers';
import {instanceGusaBackend} from 'libs/api';

export type DiscountPriceType = {
    status: string;
    coupon: string;
    price: number;
    finalPrice: number;
};

export const initialState = {
    status: '',
    coupon: '',
    price: 0,
    finalPrice: 0,
};

let discountPriceCancelSource: CancelTokenSource | null = null;

export const fetchDiscountPrice = createAction(
    'GET_DISCOUNT_PRODUCT_PRICE',
    (data: AddToCartType) => {
        if (discountPriceCancelSource) {
            discountPriceCancelSource.cancel();
        }
        // Cancels request if the previous one is not resolved
        discountPriceCancelSource = request.CancelToken.source();

        // I have no idea why backend wants options in format options[NODE_ID]: NODE_CHILD_ID instead {[NODE_ID]: NODE_CHILD_ID}
        // It's doesn't work at both cases -_- (the last one was broken in latest backend changes)
        const options = {};
        Object.keys(data.options || {}).map(
            key => (options[`options[${key}]`] = data.options && data.options[key]),
        );
        return instanceGusaBackend({
            cancelToken: discountPriceCancelSource.token,
            method: 'get',
            url: ENDPOINTS.PRODUCT_GET_DISCOUNT_PRICE({productId: data.productId}),
            params: {...omit(['options'], data), ...options, version: 'v2.0'},
        }).catch(error => {
            if (!request.isCancel(error)) {
                throw error;
            }
        });
    },
);

/**
 * Action to manually set price data (uses ONLY when product was fetched for the first time)
 */
export const setDiscountPriceData = createAction(
    'SET_DISCOUNT_PRICE_DATA',
    (data: DiscountPriceType) => data,
);

export const discountPrice = createReducer<DiscountPriceType>(
    {
        [`${fetchDiscountPrice}_FULFILLED`]: (state, payload): DiscountPriceType => {
            if (!payload) {
                // request was cancelled
                return state;
            }
            return {
                ...state,
                ...payload.data,
                status: 'fulfilled',
            };
        },
        [`${setDiscountPriceData}`]: (state, payload): DiscountPriceType => ({...payload}),
        [`${fetchDiscountPrice}_PENDING`]: (state): DiscountPriceType => ({
            ...state,
            status: 'pending',
        }),
        [`${fetchDiscountPrice}_REJECTED`]: (state): DiscountPriceType => ({
            ...state,
            status: 'rejected',
        }),
    },
    initialState,
);

/**
 * Return discounted price
 *
 * @param {ApplicationStoreType} store - redux store
 * @returns {DiscountPriceType} discounted price
 */
export const getDiscountPrice = (store: ApplicationStoreType): DiscountPriceType =>
    store.wizardMs.discountPrice;

/**
 * Returns the final prices for active product
 * in case, when price is not loaded from backend returns the initial prices
 */
export const getFinalPrice = createSelector(
    getDiscountPrice,
    (discount): DiscountPriceType => ({
        ...discount,
        price: discount.price,
        finalPrice: discount.finalPrice,
    }),
);

/**
 * Return request status
 * @return {boolean} - fulfilled or not
 */
export const isFulfilledDiscountPrice = createSelector(
    getDiscountPrice,
    ({status}: DiscountPriceType): boolean => status === 'fulfilled',
);
